// frontend/src/components/Samples/SampleOrderForm.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  sampleOrdersAPI,
  customersAPI,
  divisionsAPI,
  productionTypesAPI,
  sampleOrderTypesAPI,
  collectionsAPI,
  horzGroupsAPI,
  horzValuesAPI,
  attachmentsAPI
} from '../../api/client';
import Toast from '../Common/Toast';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../context/TranslationContext';
import SearchableSelect from '../Common/SearchableSelect';
import FileUpload from '../Common/FileUpload';
import LineAttachmentsModal from './LineAttachmentsModal';
import { useAttachmentCounts } from '../../hooks/useAttachmentCounts';
import { formatRemoveDecimals } from "../../utils/genericFunctions";
import './SampleOrderForm.css';
import { FiSave, FiX, FiPlus, FiTrash2, FiPaperclip, FiMessageSquare, FiClock, FiCopy, FiFileText, FiList, FiMail, FiGitBranch } from 'react-icons/fi';
import SampleOrderChainPanel from './SampleOrderChainPanel';
import CommentsPanel from '../Common/Comments/CommentsPanel';
import { commentsAPI } from '../../api/client';
import AuditLogPanel from '../Common/AuditLog/AuditLogPanel';
import LineOperationsModal from './LineOperationsModal';
import { FiCheckSquare } from 'react-icons/fi';
import RichTextEditor from '../Common/RichTextEditor';


const ENTITY_TYPES = {
  SAMPLE_ORDER_HEADER: 'sample_order_header',
  SAMPLE_ORDER_LINE: 'sample_order_line',
};

const SampleOrderForm = () => {
  const location = useLocation();

  const { t, currentLanguage } = useTranslation();
  const navigate = useNavigate();
  const { sampleOrderId: sampleOrderPublicId } = useParams();
  const sampleOrderId = sampleOrderPublicId; // alias — value is now a UUID public_id
  const isEditMode = !!sampleOrderId;

  const { toasts, removeToast, toast_success, toast_error, toast_success_confirm, toast_warning, toast_warning_confirm } = useToast();
  const { user: authUser } = useAuth();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [exportingFullPdf, setExportingFullPdf] = useState(false);
  const [error, setError] = useState(null);
  const [internalOrderId, setInternalOrderId] = useState(null); // internal DB id — only for attachment/comment/audit components

  const [tasksModal, setTasksModal] = useState(null);


  // Lookup data
  const [customers, setCustomers] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [productionTypes, setProductionTypes] = useState([]);
  const [sampleOrderTypes, setSampleOrderTypes] = useState([]);
  const [collections, setCollections] = useState([]);
  const [sizeTables, setSizeTables] = useState([]);
  const [sizes, setSizes] = useState({});

  const [showCommentsPanel, setShowCommentsPanel] = useState(false);
  const [commentCount, setCommentCount] = useState(0);

  const [showAuditLog, setShowAuditLog] = useState(false);
  const [showChainPanel, setShowChainPanel] = useState(false);
  const [showLineAuditLog, setShowLineAuditLog] = useState(false);
  const [selectedLineForAudit, setSelectedLineForAudit] = useState(null);

  // Form data
  const [header, setHeader] = useState({
    sampleOrderNo: '',
    customerId: '',
    externalDocument: '',
    orderDate: new Date().toISOString().split('T')[0],
    requestedDeliveryDate: '',
    priority: 2,
    sampleOrderDescription: '',
    isBillable: false,
    billTo: '',
    shipTo: '',
    status: 'OPEN',
    divisionId: '',
    productionTypeId: '',
    sampleOrderTypeId: '',
    collectionId: '',
    notes: '',
    subject: '',
    releasedAt: '',
    closedAt: '',
    cancelledAt: '',
    createdByName: ''
  });
  const hasChain = isEditMode && (!!header.parentSampleOrderNo || !!header.hasChildren);

  // Lines - now only article lines, no more scale lines
  const [lines, setLines] = useState([]);
  const [nextMatrixLineNo, setNextMatrixLineNo] = useState(1);

  // Pending attachments state
  const [pendingHeaderFiles, setPendingHeaderFiles]   = useState([]);
  const [pendingNoteFiles, setPendingNoteFiles]       = useState([]);
  const [pendingCommentFiles, setPendingCommentFiles] = useState([]);
  const [pendingLineFiles, setPendingLineFiles]       = useState({});

  // Attachment modal state
  const [showLineAttachmentsModal, setShowLineAttachmentsModal] = useState(false);
  const [selectedLineForAttachments, setSelectedLineForAttachments] = useState(null);

  const [currentLine, setCurrentLine] = useState(null);
  const [currentLineIndex, setCurrentLineIndex] = useState(null);
  const [lineAttachmentCounts, setLineAttachmentCounts] = useState({});

  // State for Article Selection
  const [selectedArticles, setSelectedArticles] = useState(new Set());
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [followUpData, setFollowUpData] = useState({
    externalDocument: '',
    orderDate: new Date().toISOString().split('T')[0],
    requestedDeliveryDate: '',
    priority: 2,
    sampleOrderDescription: '',
    divisionId: '',
    productionTypeId: '',
    sampleOrderTypeId: '',
    collectionId: '',
    notes: '',
    createdByName: ''
  });

  const existingLineIds = lines
    .filter(l => l.sample_order_line_id)
    .map(l => l.sample_order_line_id);

  const { counts: attachmentCounts, refreshCounts } = useAttachmentCounts(
    ENTITY_TYPES.SAMPLE_ORDER_LINE,
    existingLineIds
  );

  useEffect(() => {
    loadLookupData();
    if (isEditMode) {
      loadSampleOrder();
    }
  }, [sampleOrderId]);




  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showFollowUpModal) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [showFollowUpModal]);






  const loadLookupData = async () => {
    try {
      const [
        customersRes,
        divisionsRes,
        productionTypesRes,
        sampleOrderTypesRes,
        collectionsRes,
        sizeTablesRes
      ] = await Promise.all([
        customersAPI.getAll(),
        divisionsAPI.getAll(),
        productionTypesAPI.getAll(),
        sampleOrderTypesAPI.getAll(),
        collectionsAPI.getAll(),
        horzGroupsAPI.getAll()
      ]);

      if (customersRes.data.success) setCustomers(customersRes.data.data);
      if (divisionsRes.data.success) setDivisions(divisionsRes.data.data);
      if (productionTypesRes.data.success) setProductionTypes(productionTypesRes.data.data);
      if (sampleOrderTypesRes.data.success) setSampleOrderTypes(sampleOrderTypesRes.data.data);
      if (collectionsRes.data.success) setCollections(collectionsRes.data.data);
      if (sizeTablesRes.data.success) setSizeTables(sizeTablesRes.data.data);
    } catch (err) {
      console.error('Error loading lookup data:', err);
      setError('Failed to load form data');
    }
  };

  const loadSampleOrder = async () => {
    try {
      setLoading(true);

      // Clear selected articles when loading a new order
      setSelectedArticles(new Set());

      const response = await sampleOrdersAPI.getById(sampleOrderId);

      if (response.data.success) {
        const { header: headerData, lines: linesData } = response.data.data;

        setInternalOrderId(headerData.sample_order_id);
      loadCommentCount(headerData.sample_order_id);
        setHeader({
          sampleOrderNo: headerData.sample_order_no,
          customerId: headerData.customer_id,
          externalDocument: headerData.external_document,
          orderDate: headerData.order_date ? headerData.order_date.split('T')[0] : '',
          requestedDeliveryDate: headerData.requested_delivery_date ? headerData.requested_delivery_date.split('T')[0] : '',
          priority: headerData.priority,
          sampleOrderDescription: headerData.sample_order_description,
          isBillable: headerData.is_billable,
          billTo: headerData.bill_to || '',
          shipTo: headerData.ship_to || '',
          status: headerData.status,
          divisionId: headerData.division_id,
          productionTypeId: headerData.production_type_id,
          sampleOrderTypeId: headerData.sample_order_type_id,
          collectionId: headerData.collection_id || '',
          parentSampleOrderNo: headerData.parent_sample_order_no || '',
          hasChildren:         headerData.has_children || false,
          notes: headerData.notes || '',
          subject: headerData.subject || '',
          releasedAt: headerData.released_at ? headerData.released_at.split('T')[0] : '',
          closedAt: headerData.closed_at ? headerData.closed_at.split('T')[0] : '',
          cancelledAt: headerData.cancelled_at ? headerData.cancelled_at.split('T')[0] : '',
          commentsToCustomer: headerData.comments_to_customer || '',
          createdByName: headerData.created_by_name || '',
          createdAt: headerData.created_at || ''
        });

        // Group lines by matrix_line_no
        // Now we only have article lines, group them to reconstruct quantities
        const groupedByArticle = {};
        linesData.forEach(line => {
          const matrixKey = line.matrix_line_no;

          if (!groupedByArticle[matrixKey]) {
            groupedByArticle[matrixKey] = {
              lineType: 'article',
              horzComponentGroupCode: line.horz_group || '__NONE__',
              enteredProductCode: line.entered_product_code,
              enteredProductColor: line.entered_product_color,
              erpProductCode: line.erp_product_code,
              erpProductColorCode: line.erp_product_color_code,
              matrixLineNo: line.matrix_line_no,
              lineDescription: line.line_description,
              lineStatus: line.line_status,
              customerFeedback: line.customer_feedback || '', 
              quantities: {},      // { S: 5, M: 10, L: 8 }
              lineIds: {},         // ✅ { S: 101, M: 102, L: 103 }
              isDirty: false
            };
          }

          // Store quantity and line ID for this size
          const size = line.entered_product_size;
          const sizeKey = size || '__NONE__';
          groupedByArticle[matrixKey].quantities[sizeKey] = line.quantity || 0;
          groupedByArticle[matrixKey].lineIds[sizeKey] = line.sample_order_line_id;  // ✅ Track DB ID
        });

        const convertedLines = Object.values(groupedByArticle);
        setLines(convertedLines);

        const maxMatrixLineNo = Math.max(...convertedLines.map(l => l.matrixLineNo), 0);
        setNextMatrixLineNo(maxMatrixLineNo + 1);

        // Load sizes for all unique group codes
        const uniqueGroupCodes = [...new Set(convertedLines.map(l => l.horzComponentGroupCode))];

        for (const groupCode of uniqueGroupCodes) {
          if (groupCode && groupCode !== '__NONE__') {
            await loadSizesForGroup(groupCode);
          }
        }

        // Load header attachments count
        //const headerAttachmentsResult = await attachmentsAPI.getAttachments(
        //  ENTITY_TYPES.SAMPLE_ORDER_HEADER,
        //  header.sample_order_id
        //);

        //if (headerAttachmentsResult.success) {
        //  setHeaderAttachmentCount(headerAttachmentsResult.attachments.length);
        //}

        // Load line attachments
        //await loadLineAttachments(lines);  // ADD THIS LINE
        //Load line attachments AFTER state is set
        console.log('📎 About to load line attachments for convertedLines:', convertedLines);
        await loadLineAttachments(convertedLines);  // Pass the array directly, not the state        



      }
    } catch (err) {
      console.error('Error loading sample order:', err);
      setError('Failed to load sample order');
    } finally {
      setLoading(false);
    }
  };




  const loadLineAttachments = async (lines) => {
  try {
    console.log('📎 Starting optimized attachment count loading');

    if (!lines || lines.length === 0) {
      return;
    }

    // Collect all line IDs and map them back to articles
    const lineIdToArticleMap = {};
    const allLineIds = [];

    lines.forEach(article => {
      if (article.lineIds && Object.keys(article.lineIds).length > 0) {
        const lineIds = Object.values(article.lineIds).filter(id => id);
        lineIds.forEach(lineId => {
          lineIdToArticleMap[lineId] = article.matrixLineNo;
          allLineIds.push(lineId);
        });
      }
    });

    if (allLineIds.length === 0) {
      console.log('⏭️ No line IDs to check');
      return;
    }

    console.log(`📎 Fetching counts for ${allLineIds.length} line IDs in one batch call`);

    // Single batch API call for all counts
    const result = await attachmentsAPI.getAttachmentCounts(
      ENTITY_TYPES.SAMPLE_ORDER_LINE,
      allLineIds
    );

    if (result.data?.success && result.data.counts) {
      const counts = {};
      
      // Aggregate counts by article (matrixLineNo)
      Object.entries(result.data.counts).forEach(([lineId, count]) => {
        const matrixLineNo = lineIdToArticleMap[lineId];
        if (matrixLineNo) {
          counts[matrixLineNo] = (counts[matrixLineNo] || 0) + count;
        }
      });

      console.log('✅ Attachment counts loaded:', counts);
      setLineAttachmentCounts(counts);
    }

  } catch (error) {
    console.error('❌ Error loading attachment counts:', error);
  }
};


  const loadSizesForGroup = async (groupCode) => {
    try {
      const response = await horzValuesAPI.getAll(groupCode);
      if (response.data.success) {
        setSizes(prev => ({
          ...prev,
          [groupCode]: response.data.data.sort((a, b) => a.sorting - b.sorting)
        }));
      }
    } catch (err) {
      console.error(`Error loading sizes for ${groupCode}:`, err);
    }
  };

  // Add with other functions
  const loadCommentCount = async (id) => {
    const entityId = id || internalOrderId;
    if (!isEditMode || !entityId) return;
    try {
      const response = await commentsAPI.getComments('sample_order_header', entityId);
      if (response.data.success) {
        setCommentCount(response.data.data?.length || 0);
      }
    } catch (error) {
      console.error('Error loading comment count:', error);
    }
  };

  // Toast-confirm helper for RTE file deletion
  const handleRteConfirmDelete = (message, onConfirm) => {
    toast_warning_confirm(message, onConfirm);
  };

  const handleEmailComments = async () => {
    if (!sampleOrderId) return;
    try {
      setSendingEmail(true);
      const res = await sampleOrdersAPI.emailComments(sampleOrderId);
      if (res.data.success) {
        toast_success(
          `${t('email.sent', 'Email enviado')} → ${authUser?.email || ''}` +
          (res.data.attachmentCount > 0
            ? ` (${res.data.attachmentCount} ${t('email.attachments', 'anexo(s)')})`
            : '')
        );
      } else {
        toast_error(res.data.error || t('email.error', 'Erro ao enviar email'));
      }
    } catch (err) {
      toast_error(err.response?.data?.error || t('email.error', 'Erro ao enviar email'));
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!sampleOrderId) return;
    try {
      setExportingPdf(true);
      const response = await sampleOrdersAPI.downloadPdf(sampleOrderId, currentLanguage);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `SampleOrder_${header.sampleOrderNo || sampleOrderId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast_error(t('messages.error_pdf', 'Failed to generate PDF'));
      console.error('PDF export error:', err);
    } finally {
      setExportingPdf(false);
    }
  };

  const handleDownloadFullPdf = async () => {
    try {
      setExportingFullPdf(true);
      const response = await sampleOrdersAPI.downloadFullPdf(sampleOrderId, currentLanguage);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SampleOrder_${header.sampleOrderNo || sampleOrderId}_Full.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast_error(t('messages.error_pdf', 'Failed to generate PDF'));
      console.error('Full PDF export error:', err);
    } finally {
      setExportingFullPdf(false);
    }
  };

  const handleHeaderChange = (field, value) => {
    setHeader(prev => ({ ...prev, [field]: value }));
  };

  const handleAddScale = async () => {
    // Now we just add the first article line, no scale line
    const matrixLineNo = nextMatrixLineNo;

    const articleLine = {
      lineType: 'article',
      horzComponentGroupCode: '',
      enteredProductCode: '',
      enteredProductColor: '',
      erpProductCode: '',
      erpProductColorCode: '',
      matrixLineNo: matrixLineNo,
      lineNo: 1,
      lineDescription: '',
      lineStatus: 'OPEN',
      quantities: {},
      lineIds: {},      // Empty for new article
      isDirty: true  // New lines are dirty      
    };

    setLines(prev => [...prev, articleLine]);
    setNextMatrixLineNo(matrixLineNo + 1);
  };

  // Simplified: Just add article with next matrix number
  const handleAddArticle = () => {
    const newArticle = {
      lineType: 'article',
      horzComponentGroupCode: '',
      enteredProductCode: '',
      enteredProductColor: '',
      erpProductCode: '',
      erpProductColorCode: '',
      matrixLineNo: nextMatrixLineNo,  // Each article gets unique number
      lineDescription: '',
      lineStatus: 'OPEN',
      quantities: {},
      lineIds: {},
      isDirty: true
    };

    setLines(prev => [...prev, newArticle]);
    setNextMatrixLineNo(nextMatrixLineNo + 1);
  };


  // Update handleLineChange - when size table changes, update sizes
  const handleLineChange = async (index, field, value) => {
    const updatedLines = [...lines];
    updatedLines[index][field] = value;
    updatedLines[index].isDirty = true;

    // If changing size table, load sizes and initialize quantities
    if (field === 'horzComponentGroupCode') {
      if (value === '__NONE__') {
        updatedLines[index].quantities = { '__NONE__': 0 };
      } else if (value) {
        await loadSizesForGroup(value);
        const sizeColumns = sizes[value] || [];
        const initialQuantities = {};
        sizeColumns.forEach(size => {
          initialQuantities[size.horz_value] = updatedLines[index].quantities[size.horz_value] || '';
        });
        updatedLines[index].quantities = initialQuantities;
      } else {
        updatedLines[index].quantities = {};
      }
    }

    setLines(updatedLines);
  };


  const handleQuantityChange = (index, sizeCode, value) => {
    const updatedLines = [...lines];
    const newQty = value === '' ? 0 : parseFloat(value) || 0;

    // If quantity changed, mark as dirty
    if (updatedLines[index].quantities[sizeCode] !== newQty) {
      updatedLines[index].quantities[sizeCode] = newQty;
      updatedLines[index].isDirty = true;
    }

    setLines(updatedLines);
  };


  // Simplified delete - just remove the article
  const handleDeleteLine = (index) => {
    if (window.confirm(t('sample_orders.confirm_delete_article', 'Delete this article?'))) {
      setLines(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleDuplicateLine = (index) => {
    const original = lines[index];
    const newLine = { ...original, matrixLineNo: nextMatrixLineNo, lineIds: {}, isDirty: true };
    setLines(prev => [...prev, newLine]);
    setNextMatrixLineNo(prev => prev + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!header.customerId) {
      toast_error(t('validation.customer_required', 'Customer is required'));
      return;
    }

    if (!header.divisionId || !header.productionTypeId || !header.sampleOrderTypeId) {
      toast_error(t('validation.required_fields', 'Please fill in all required fields'));
      return;
    }

    try {
      setSaving(true);

      // Send lines in grouped format (with quantities AND lineIds)
      const data = {
        header: {
          customerId: header.customerId,
          externalDocument: header.externalDocument,
          orderDate: header.orderDate,
          requestedDeliveryDate: header.requestedDeliveryDate,
          priority: header.priority,
          sampleOrderDescription: header.sampleOrderDescription,
          isBillable: header.isBillable,
          billTo: header.billTo,
          shipTo: header.shipTo,
          status: header.status,
          divisionId: header.divisionId,
          productionTypeId: header.productionTypeId,
          sampleOrderTypeId: header.sampleOrderTypeId,
          collectionId: header.collectionId || null,
          notes: header.notes,
          commentsToCustomer: header.commentsToCustomer || null,
          subject: header.subject || null,
        },
        lines: lines.map(line => ({
          matrixLineNo: line.matrixLineNo,
          horzComponentGroupCode: line.horzComponentGroupCode,
          enteredProductCode: line.enteredProductCode,
          enteredProductColor: line.enteredProductColor,
          erpProductCode: line.erpProductCode,
          erpProductColorCode: line.erpProductColorCode,
          lineDescription: line.lineDescription,
          lineType: 'article',
          lineStatus: line.lineStatus || 'OPEN',
          customer_feedback: line.customerFeedback || null ,
          quantities: line.quantities,  // ✅ Keep as object
          lineIds: line.lineIds || {},   // ✅ Include lineIds mapping
          isDirty: line.isDirty
        }))
      };

      let response;
      let orderIdForAttachments;
      let returnedLines = [];

      if (isEditMode) {
        response = await sampleOrdersAPI.update(sampleOrderId, data);
        orderIdForAttachments = internalOrderId; // internal id for attachment system

        // Backend doesn't return lines anymore in the new format
        // We'll need to reload to get new lineIds
      } else {
        response = await sampleOrdersAPI.create(data);

        if (response.data.success) {
          // Use integer sample_order_id — attachmentsAPI requires the internal numeric id
          orderIdForAttachments = response.data.data.header.sample_order_id;
          returnedLines = response.data.data.lines;
        }
      }

      // Upload pending attachments
      if (response.data.success) {
        try {
          // Upload header attachments
          if (pendingHeaderFiles.length > 0) {
            await attachmentsAPI.upload(
              ENTITY_TYPES.SAMPLE_ORDER_HEADER,
              orderIdForAttachments,
              pendingHeaderFiles,
              '',
              '',
              header.sampleOrderNo || response.data.data?.header?.sample_order_no
            );
            setPendingHeaderFiles([]);
          }
          if (pendingNoteFiles.length > 0) {
            await attachmentsAPI.upload(ENTITY_TYPES.SAMPLE_ORDER_HEADER,
              orderIdForAttachments, pendingNoteFiles, '', 'notes',
              header.sampleOrderNo || response.data.data?.header?.sample_order_no);
            setPendingNoteFiles([]);
          }
          if (pendingCommentFiles.length > 0) {
            await attachmentsAPI.upload(ENTITY_TYPES.SAMPLE_ORDER_HEADER,
              orderIdForAttachments, pendingCommentFiles, '', 'comments_to_customer',
              header.sampleOrderNo || response.data.data?.header?.sample_order_no);
            setPendingCommentFiles([]);
          }

          // Upload line attachments
          for (const [lineKey, files] of Object.entries(pendingLineFiles)) {
            if (files.length > 0) {
              const [matrixNo, lineNo] = lineKey.split('-').map(Number);

              // For create: Find the matching returned line to get its ID
              if (!isEditMode) {
                const matchingLine = returnedLines.find(
                  rl => rl.matrix_line_no === matrixNo && rl.line_no === lineNo
                );

                if (matchingLine) {
                  await attachmentsAPI.upload(
                    ENTITY_TYPES.SAMPLE_ORDER_LINE,
                    matchingLine.sample_order_line_id,
                    files,
                    '',
                    '',
                    header.sampleOrderNo || response.data.data?.header?.sample_order_no
                  );
                }
              } else {
                // For edit: Use the lineIds from our state
                const line = lines.find(l => l.matrixLineNo === matrixNo);
                if (line && line.lineIds) {
                  // Upload for the first size's line ID (they're all the same article)
                  const firstLineId = Object.values(line.lineIds)[0];
                  if (firstLineId) {
                    await attachmentsAPI.upload(
                      ENTITY_TYPES.SAMPLE_ORDER_LINE,
                      firstLineId,
                      files,
                      '',
                      '',
                      header.sampleOrderNo
                    );
                  }
                }
              }
            }
          }
          setPendingLineFiles({});



// Upload pending inline images from sampleOrderDescription - removed
// Images are now stored as base64 inline in the HTML — no upload needed.






          //toast_success(response.data.message);
          toast_success(t('messages.success_save', 'Gravado com sucesso'));
          

          // Navigate: edit mode reloads; new order navigates to the saved order
          if (isEditMode) {
            await loadSampleOrder();
          } else {
            const newPublicId = response.data.data.header.public_id;
            navigate(`/samples/orders/${newPublicId}`);
          }

        } catch (uploadErr) {
          console.error('Error uploading attachments:', uploadErr);
          toast_warning(t('attachments.upload_error', 'Order saved but some files failed to upload'));
          if (!isEditMode) {
            const newPublicId = response.data.data?.header?.public_id;
            navigate(newPublicId ? `/samples/orders/${newPublicId}` : '/samples/orders');
          }
        }
      }

    } catch (err) {
      console.error('Error saving sample order:', err);
      toast_error(err.response?.data?.error || 'Failed to save sample order');
    } finally {
      setSaving(false);
    }
  };


    const handleExit = () => {
    // Check if we came from article list
    if (location.state?.returnTo && location.state?.returnState) {
      navigate(location.state.returnTo, {
        state: location.state.returnState
      });
    } else {
      // Default: go to orders list
      navigate('/samples/orders');
    }
  };


  // Get the first line ID from the article (any size will do since they're all part of same article)
  const getFirstLineId = (article) => {
    if (!article || !article.lineIds) return null;
    const lineIds = Object.values(article.lineIds);
    return lineIds.length > 0 ? lineIds[0]?.toString() : null;
  };

  const handleLinePendingFilesChange = (lineKey, files) => {
    setPendingLineFiles(prev => ({
      ...prev,
      [lineKey]: files
    }));
  };

  /*
  const handleOpenLineAttachments = (line) => {
    const lineKey = `${line.matrixLineNo}-${line.lineNo}`;
    setSelectedLineForAttachments({ ...line, lineKey });
    setShowLineAttachmentsModal(true);
  };
  */

  const handleOpenLineAttachments = (article, articleIndex) => {
    console.log('Opening attachments for article:', article);

    // Get the first line ID (any size will work since attachments are per article)
    const firstLineId = article.lineIds ? Object.values(article.lineIds)[0] : null;

    if (!firstLineId && !isEditMode) {
      // For new articles (not yet saved), we'll handle pending files
      setCurrentLine(article);
      setCurrentLineIndex(articleIndex);
      setShowLineAttachmentsModal(true);
      return;
    }

    // For saved articles, we need to pass the line ID
    setCurrentLine({
      ...article,
      sample_order_line_id: firstLineId // Use first line ID for loading attachments
    });
    setCurrentLineIndex(articleIndex);
    setShowLineAttachmentsModal(true);
  };


  const handleCloseLineAttachments = () => {
    setShowLineAttachmentsModal(false);
    setSelectedLineForAttachments(null);
    refreshCounts();
  };


  const handleArticleCheckboxChange = (matrixLineNo, checked) => {
    if (checked) {
      const article = lines.find(l => l.matrixLineNo === matrixLineNo);
      if (article && !article.customerFeedback) {
        toast_warning(t('sample_orders.feedback_required_warning', 'Article selected but Customer Feedback is not filled in'));
      }
    }
    setSelectedArticles(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(matrixLineNo);
      } else {
        newSet.delete(matrixLineNo);
      }
      return newSet;
    });
  };


  const handleOpenFollowUpModal = () => {
    if (selectedArticles.size === 0) {
      toast_warning(t('sample_orders.select_articles_first', 'Please select at least one article'));
      return;
    }

    // Pre-fill form with current order data
    setFollowUpData({
      externalDocument: header.externalDocument || '',
      orderDate: new Date().toISOString().split('T')[0],
      requestedDeliveryDate: '',
      priority: header.priority || 2,
      sampleOrderDescription: '',
      divisionId: header.divisionId || '',
      productionTypeId: header.productionTypeId || '',
      sampleOrderTypeId: header.sampleOrderTypeId || '',
      collectionId: header.collectionId || '',
      notes: ''
    });

    setShowFollowUpModal(true);
  };


  const handleFollowUpDataChange = (field, value) => {
    setFollowUpData(prev => ({ ...prev, [field]: value }));
  };



  const handleCreateFollowUp = async () => {
    if (!followUpData.sampleOrderDescription) {
      toast_error(t('validation.description_required', 'Description is required'));
      return;
    }

    try {
      setSaving(true);

      const selectedArticlesList = lines.filter(line =>
        selectedArticles.has(line.matrixLineNo)
      );

      const response = await sampleOrdersAPI.createFollowUp(sampleOrderId, {
        followUpData: {
          customerId: header.customerId,
          externalDocument: followUpData.externalDocument,
          orderDate: followUpData.orderDate,
          requestedDeliveryDate: followUpData.requestedDeliveryDate,
          priority: followUpData.priority,
          sampleOrderDescription: followUpData.sampleOrderDescription,
          isBillable: header.isBillable,
          billTo: header.billTo,
          shipTo: header.shipTo,
          status: 'OPEN',
          divisionId: followUpData.divisionId,
          productionTypeId: followUpData.productionTypeId,
          sampleOrderTypeId: followUpData.sampleOrderTypeId,
          collectionId: followUpData.collectionId,
          notes: followUpData.notes,
          parentSampleOrderId: internalOrderId,
          parentSampleOrderNo: header.sampleOrderNo
        },
        selectedArticles: selectedArticlesList
      });

      if (response.data.success) {
        toast_success(t('sample_orders.follow_up_created', 'Follow-up order created successfully'));

        // Clear selections before closing modal and navigating
        setSelectedArticles(new Set());
        setShowFollowUpModal(false);

        // Navigate to new order
        const newOrderId = response.data.data.orderId;
        navigate(`/samples/orders/${newOrderId}`);
      }

    } catch (error) {
      console.error('Error creating follow-up order:', error);
      toast_error(error.response?.data?.error || t('sample_orders.follow_up_error', 'Failed to create follow-up order'));
    } finally {
      setSaving(false);
    }
  };






  if (loading) {
    return (
      <div className="page-container">
        <div className="loading">{t('loading', 'Loading...')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="error">{error}</div>
      </div>
    );
  }


  return (
    <div className="page-container">

      {/* Sticky Header - First element, no wrapper */}
      <div className="sticky-header-ribbon">
        <div className="sticky-header-content">
          <div className="sticky-header-left">
            <div className="order-info">
              <span className="order-label">
                {isEditMode
                  ? t('sample_orders.edit', 'Edit Sample Order')
                  : t('sample_orders.create', 'Create Sample Order')}
              </span>
              {isEditMode && header.sampleOrderNo && (
                <span className="order-number">#{header.sampleOrderNo}</span>
              )}
            </div>
          </div>

          <div className="sticky-header-right">

            {/* Add Article */}
            <button
              type="button"
              onClick={handleAddScale}
              className="btn-secondary btn"
            >
              <FiPlus size={14} />
              {t('sample_orders.add_article', 'Add Article')}
            </button>

            
            {/* Comments Button */}
            {isEditMode && (
              <button
                type="button"
                onClick={() => setShowCommentsPanel(true)}
                className={`btn btn-secondary btn-icon${commentCount > 0 ? ' btn--has-comments' : ''}`}
                title={t('comments.view_comments', 'Comentários')}
              >
                <FiMessageSquare size={18} />
              </button>
            )}
            {/* History Button */}
            {isEditMode && (
              <button
                type="button"
                onClick={() => setShowAuditLog(true)}
                className="btn btn-secondary btn-icon"
                title={t('audit.view_history', 'View Change History')}
              >
                <FiClock size={18} />
              </button>
            )}

            {/* Order Chain Button */}
            {isEditMode && (
              <button
                type="button"
                onClick={() => setShowChainPanel(true)}
                className={`btn btn-secondary btn-icon${hasChain ? ' btn--has-chain' : ''}`}
                title="Sequência de Pedidos"
              >
                <FiGitBranch size={18} />
              </button>
            )}

            {/* PDF Export Button */}
            {isEditMode && (
              <button
                type="button"
                onClick={handleDownloadPdf}
                className="btn btn-secondary"
                disabled={exportingPdf}
                title={t('sample_orders.export_pdf', 'Export PDF')}
              >
                <FiFileText size={16} />
                {exportingPdf ? '...' : 'PDF'}
              </button>
            )}

            {/* Full Report Button */}
            {isEditMode && (
              <button
                type="button"
                onClick={handleDownloadFullPdf}
                className="btn btn-secondary"
                disabled={exportingFullPdf}
                title={t('sample_orders.export_full_pdf', 'Relatório Completo')}
              >
                <FiFileText size={16} />
                {exportingFullPdf ? '...' : t('sample_orders.full_report', 'Relatório Completo')}
              </button>
            )}

            {/* Save Button */}
            <button
              type="submit"
              form="sample-order-form"
              className="btn btn-primary"
              disabled={saving}
            >
              <FiSave size={16} />
              {saving ? t('saving', 'Saving...') : t('save', 'Save')}
            </button>

            {/* Exit Button */}
            <button
              type="button"
              onClick={() => handleExit()}
              className="btn btn-secondary"
            >
              <FiX size={16} />
              {t('exit', 'Sair')}
            </button>
          </div>
        </div>
      </div>



      <form id="sample-order-form" onSubmit={handleSubmit}>
        {/* Header Section */}
        <div className="form-section">
          <h3 className="section-title-medium">
            {t('sample_orders.header_info', 'Header Information')}
          </h3>

          <div className="form-grid form-grid-4">
            <div className="form-group">
              <label>{t('sample_orders.customer', 'Customer')} *</label>
              <SearchableSelect
                value={header.customerId}
                onChange={(value) => handleHeaderChange('customerId', value)}
                options={customers.map(c => ({
                  value: c.customer_id,
                  code: c.customer_code,
                  name: c.customer_name
                }))}
                getOptionLabel={(opt) => opt.name ? `${opt.code} - ${opt.name}` : opt.code}
                getOptionValue={(opt) => opt.value}
                formatDisplay={(opt) => opt.name ? `${opt.code} - ${opt.name}` : opt.code}
                placeholder={t('select', 'Select...')}
                required
              />
            </div>

            <div className="form-group form-group-small">
              <label>{t('sample_orders.external_doc', 'External Doc')}</label>
              <input
                type="text"
                value={header.externalDocument}
                onChange={(e) => handleHeaderChange('externalDocument', e.target.value)}
                maxLength={20}
              />
            </div>

            <div className="form-group form-group-small">
              <label>{t('sample_orders.order_date', 'Order Date')}</label>
              <input
                type="date"
                value={header.orderDate}
                onChange={(e) => handleHeaderChange('orderDate', e.target.value)}
              />
            </div>

            <div className="form-group form-group-small">
              <label>{t('sample_orders.delivery_date', 'Delivery')}</label>
              <input
                type="date"
                value={header.requestedDeliveryDate}
                onChange={(e) => handleHeaderChange('requestedDeliveryDate', e.target.value)}
              />
            </div>

                        <div className="form-group form-group-medium">
              <label>{t('sample_orders.order_type', 'Order Type')} *</label>
              <SearchableSelect
                value={header.sampleOrderTypeId}
                onChange={(value) => handleHeaderChange('sampleOrderTypeId', value)}
                options={sampleOrderTypes.map(sot => ({
                  value: sot.sample_order_type_id,
                  code: sot.sample_order_type,
                  desc: sot.description
                }))}
                getOptionLabel={(opt) => opt.desc ? `${opt.code} - ${opt.desc}` : opt.code}
                getOptionValue={(opt) => opt.value}
                formatDisplay={(opt) => opt.desc ? `${opt.code} - ${opt.desc}` : opt.code}
                placeholder={t('select', 'Select...')}
                required
              />
            </div>



            <div className="form-group form-group-medium">
              <label>{t('sample_orders.division', 'Division')} *</label>
              <SearchableSelect
                value={header.divisionId}
                onChange={(value) => handleHeaderChange('divisionId', value)}
                options={divisions.map(d => ({
                  value: d.division_id,
                  code: d.division_code,
                  desc: d.description
                }))}
                getOptionLabel={(opt) => opt.desc ? `${opt.code} - ${opt.desc}` : opt.code}
                getOptionValue={(opt) => opt.value}
                formatDisplay={(opt) => opt.desc ? `${opt.code} - ${opt.desc}` : opt.code}
                placeholder={t('select', 'Select...')}
                required
              />
            </div>

            <div className="form-group form-group-medium">
              <label>{t('sample_orders.production_type', 'Production Type')} *</label>
              <SearchableSelect
                value={header.productionTypeId}
                onChange={(value) => handleHeaderChange('productionTypeId', value)}
                options={productionTypes.map(pt => ({
                  value: pt.production_type_id,
                  code: pt.production_type,
                  desc: pt.description
                }))}
                getOptionLabel={(opt) => opt.desc ? `${opt.code} - ${opt.desc}` : opt.code}
                getOptionValue={(opt) => opt.value}
                formatDisplay={(opt) => opt.desc ? `${opt.code} - ${opt.desc}` : opt.code}
                placeholder={t('select', 'Select...')}
                required
              />
            </div>

            <div className="form-group form-group-small">
              <label>{t('sample_orders.priority', 'Priority')}</label>
              <select
                value={header.priority}
                onChange={(e) => handleHeaderChange('priority', parseInt(e.target.value))}
              >
                <option value={1}>Alta</option>
                <option value={2}>Média</option>
                <option value={3}>Baixa</option>
              </select>
            </div>


            <div className="form-group form-group-medium">
              <label>{t('sample_orders.collection', 'Collection')}</label>
              <SearchableSelect
                value={header.collectionId}
                onChange={(value) => handleHeaderChange('collectionId', value)}
                options={collections.map(col => ({
                  value: col.collection_id,
                  code: col.collection_code,
                  desc: col.description
                }))}
                getOptionLabel={(opt) => opt.desc ? `${opt.code} - ${opt.desc}` : opt.code}
                getOptionValue={(opt) => opt.value}
                formatDisplay={(opt) => opt.desc ? `${opt.code} - ${opt.desc}` : opt.code}
                placeholder={t('select', 'Select...')}
              />
            </div>

            <div className="form-group form-group-medium">
              <label>{t('sample_orders.created_by', 'Criado por')}</label>
              <input
                type="text"
                value={isEditMode ? (header.createdByName || '—') : (authUser?.full_name || authUser?.username || '—')}
                disabled
                className="input-readonly"
              />
            </div>

            <div className="form-group form-group-medium">
              <label>{t('sample_orders.parent_order', 'Parent Order')}</label>
              <input
                type="text"
                value={header.parentSampleOrderNo || ''}
                disabled
                className="input-readonly"
                placeholder="-"
              />
            </div>

            <div className="form-group form-group-small">
              <label>{t('sample_orders.status', 'Status')}</label>
              <select
                value={header.status}
                onChange={(e) => handleHeaderChange('status', e.target.value)}
              >
                <option value="OPEN">{t('status.open', 'Aberto')}</option>
                <option value="RELEASED">{t('status.released', 'Liberto')}</option>
                <option value="CLOSED">{t('status.closed', 'Fechado')}</option>
                <option value="CANCELLED">{t('status.cancelled', 'Cancelado')}</option>
                <option value="ON_HOLD">{t('status.waiting', 'Em Espera')}</option>
              </select>
            </div>            

<div className="form-group form-group-small">
  <label>{t('sample_orders.released_at', 'Data Liberto')}</label>
  <input
    type="date"
    value={header.releasedAt || ''}
    disabled
    className="input-readonly"
  />
</div>

<div className="form-group form-group-small">
  <label>{t('sample_orders.closed_at', 'Data Fecho')}</label>
  <input
    type="date"
    value={header.closedAt || ''}
    disabled
    className="input-readonly"
  />
</div>

<div className="form-group form-group-small">
  <label>{t('sample_orders.cancelled_at', 'Data Cancelamento')}</label>
  <input
    type="date"
    value={header.cancelledAt || ''}
    disabled
    className="input-readonly"
  />
</div>

<div className="form-group full-width">
  <label>{t('sample_orders.subject', 'Assunto')}</label>
  <input
    type="text"
    value={header.subject || ''}
    onChange={(e) => handleHeaderChange('subject', e.target.value)}
    maxLength={255}
  />
</div>

<div className="form-group full-width">
  <label>
    {t('sample_orders.description', 'Descrição')} *
  </label>
  <RichTextEditor
    value={header.sampleOrderDescription || ''}
    onChange={(html) => handleHeaderChange('sampleOrderDescription', html)}
    placeholder={t('sample_orders.description_placeholder', 'Descrição da encomenda… (pode colar imagens)')}
    minHeight={260}
  />
</div>

            <div className="form-group-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={header.isBillable}
                  onChange={(e) => handleHeaderChange('isBillable', e.target.checked)}
                />
                {t('sample_orders.is_billable', 'Billable')}
              </label>
            </div>

            <div className="form-group">
              <label>{t('sample_orders.bill_to', 'Bill To')}</label>
              <input
                type="text"
                value={header.billTo}
                onChange={(e) => handleHeaderChange('billTo', e.target.value)}
                maxLength={50}
              />
            </div>

            <div className="form-group">
              <label>{t('sample_orders.ship_to', 'Ship To')}</label>
              <input
                type="text"
                value={header.shipTo}
                onChange={(e) => handleHeaderChange('shipTo', e.target.value)}
                maxLength={50}
              />
            </div>

            <div className="form-group full-width">
              <label>{t('notes', 'Notas')}</label>
              <RichTextEditor
                value={header.notes || ''}
                onChange={(html) => handleHeaderChange('notes', html)}
                minHeight={120}
                pendingFiles={pendingNoteFiles}
                onPendingFilesChange={setPendingNoteFiles}
                onToast={(msg) => toast_error(msg)}
                onConfirmDelete={handleRteConfirmDelete}
                attachEntityType={isEditMode ? 'sample_order_header' : null}
                attachEntityId={isEditMode ? internalOrderId : null}
                attachCategory="notes"
              />
            </div>

            <div className="form-group full-width">
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4 }}>
                <label style={{ margin:0 }}>{t('comments_to_customer', 'Comentários para o Cliente')}</label>
                {isEditMode && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={handleEmailComments}
                    disabled={sendingEmail}
                    title={`Enviar por email para ${authUser?.email || 'o utilizador'}`}
                  >
                    <FiMail size={13} />
                    {sendingEmail ? 'A enviar…' : 'Enviar por email'}
                  </button>
                )}
              </div>
              <RichTextEditor
                value={header.commentsToCustomer || ''}
                onChange={(html) => handleHeaderChange('commentsToCustomer', html)}
                minHeight={120}
                pendingFiles={pendingCommentFiles}
                onPendingFilesChange={setPendingCommentFiles}
                onToast={(msg) => toast_error(msg)}
                onConfirmDelete={handleRteConfirmDelete}
                attachEntityType={isEditMode ? 'sample_order_header' : null}
                attachEntityId={isEditMode ? internalOrderId : null}
                attachCategory="comments_to_customer"
              />
            </div>

            <div className="form-group full-width">
              <label style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                {t('attachments.header', 'Header Attachments')}
              </label>
              <FileUpload
                entityType={ENTITY_TYPES.SAMPLE_ORDER_HEADER}
                entityId={isEditMode ? internalOrderId : null}
                maxFiles={20}
                sideBySide={true}
                compact={false}
                inline={false}
                pendingFiles={pendingHeaderFiles}
                onPendingFilesChange={setPendingHeaderFiles}
                excludeCategories={['notes', 'comments_to_customer']}
              />
            </div>
          </div>
        </div>

        {/* Lines Section - Simplified, No Grouping */}
        <div className="form-section">
          <div className="section-header">
            <h3>{t('sample_orders.lines', 'Lines')}</h3>

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {/* Select/Clear All buttons */}
              {isEditMode && lines.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      const allArticleNos = new Set(lines.map(l => l.matrixLineNo));
                      setSelectedArticles(allArticleNos);
                    }}
                    className="btn btn-secondary btn-sm"
                    style={{ fontSize: '12px', padding: '4px 12px' }}
                  >
                    {t('select_all', 'Selecionar Todos')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedArticles(new Set())}
                    className="btn btn-secondary btn-sm"
                    style={{ fontSize: '12px', padding: '4px 12px' }}
                  >
                    {t('clear_selection', 'Limpar')}
                  </button>
                </>
              )}

              {/* Create Follow-Up Order Button */}
              {isEditMode && lines.length > 0 && (
                <button
                  type="button"
                  onClick={handleOpenFollowUpModal}
                  className="btn btn-secondary btn-sm"
                  disabled={selectedArticles.size === 0}
                >
                  <FiCopy size={14} />
                  {t('sample_orders.create_follow_up', 'Criar Pedido Revisão')}
                </button>
              )}

              {/* Add Article */}
              <button
                type="button"
                onClick={handleAddScale}
                className="btn-primary btn-sm"
              >
                <FiPlus size={14} />
                {t('sample_orders.add_article', 'Add Article')}
              </button>

            </div>
          </div>

          {lines.length === 0 ? (
            <div className="no-lines">
              <p>{t('sample_orders.no_lines', 'No articles added yet. Click "Add Article" to start.')}</p>
            </div>
          ) : (
            lines.map((article, articleIndex) => {
              const isNoSize = article.horzComponentGroupCode === '__NONE__';
              const sizeColumns = (article.horzComponentGroupCode && !isNoSize)
                ? sizes[article.horzComponentGroupCode] || [] : [];
              const lineKey = article.matrixLineNo;

              return (
                <div key={article.matrixLineNo || articleIndex} className="matrix-container">


                  <div className="matrix-table-container">
                    <table className="matrix-table">
                      <thead>
                        <tr>
                          <th style={{ width: '200px' }}>{t('sample_orders.product_code', 'Product')}</th>
                          <th style={{ width: '120px' }}>{t('sample_orders.color', 'Color')}</th>
                          {sizeColumns.map(size => (
                            <th key={size.horz_value} className="text-center size-column" style={{ width: '60px' }}>
                              {size.horz_value}
                            </th>
                          ))}
                          {isNoSize && (
                            <th className="text-center size-column" style={{ width: '70px' }}>Qtd</th>
                          )}
                          <th style={{ width: '50px' }} className="text-center">
                            <FiPaperclip size={14} title={t('attachments.title', 'Attachments')} />
                          </th>
                          <th style={{ width: '50px' }} className="text-center">
                            <FiTrash2 size={14} title={t('attachments.title', 'Attachments')} />
                          </th>
                          <th style={{ width: '50px' }} className="text-center">
                            <FiClock size={14} title={t('audit.view_history', 'View Line History')} />
                          </th>
                           <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Size Table Selector Row */}
                        <tr className="scale-header-row">
                          <td colSpan={3}>
                            <div className="scale-selector">
                              {/* Article line number + checkbox for selection */}
                              {isEditMode && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span style={{
                                    fontSize: 14, fontWeight: 700, color: 'var(--gray-500)',
                                    fontFamily: 'monospace', minWidth: 18, textAlign: 'left',
                                  }}>
                                    {article.matrixLineNo}
                                  </span>
                                  <input
                                    type="checkbox"
                                    checked={selectedArticles.has(article.matrixLineNo)}
                                    onChange={(e) => handleArticleCheckboxChange(article.matrixLineNo, e.target.checked)}
                                  />
                                </div>
                              )}

                              <label>{t('sample_orders.size_table', 'Size Table')}:</label>
                              <select
                                value={article.horzComponentGroupCode || ''}
                                onChange={(e) => handleLineChange(articleIndex, 'horzComponentGroupCode', e.target.value)}
                                className="form-control-sm"
                              >
                                <option value="">{t('select', 'Select...')}</option>
                                <option value="__NONE__">Sem Tamanho</option>
                                {sizeTables.map(table => (
                                  <option key={table.horz_group_id} value={table.horz_group}>
                                    {table.description ? `${table.horz_group} — ${table.description}` : table.horz_group}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </td>


            {/* Estado Linha*/}
            <td colSpan={5} className='status-cell'>
                <label style={{ marginRight: '8px', fontWeight: 600 }}>
                  {t('sample_orders.status', 'Estado')}:
                </label>
                <select
                  value={article.lineStatus || 'open'}
                  onChange={(e) => handleLineChange(articleIndex, 'lineStatus', e.target.value)}
                  className="form-control-sm"
                  style={{ width: '180px' }}
                >
                  <option value="NEW">{t('status.open', 'Novo')}</option>
                  <option value="IN_PROGRESS">{t('status.in_progress', 'Em Curso')}</option>
                  <option value="SENT_CUSTOMER">{t('status.sent_customer', 'Enviado Cliente')}</option>
                  <option value="CLOSED">{t('status.closed', 'Fechado')}</option>
                  <option value="CANCELLED">{t('status.cancelled', 'Cancelado')}</option>
                </select>
            </td>


                          {/* Feedback Cliente */}
                          <td colSpan={6} className="feedback-cell">
                            <label style={{ marginRight: '8px', fontWeight: 600 }}>
                              {t('sample_orders.customer_feedback', 'Feedback Cliente')}:
                            </label>
                            <select
                              value={article.customerFeedback || ''}
                              onChange={(e) => handleLineChange(articleIndex, 'customerFeedback', e.target.value)}
                              className={`form-control-sm ${
                                article.customerFeedback === 'APPROVED' ? 'feedback-aprovado' :
                                article.customerFeedback === 'REJECTED' ? 'feedback-rejeitado' :
                                article.customerFeedback === 'CHANGES REQUIRED' ? 'feedback-alteracoes' : ''
                              }`}
                              style={{ width: '180px' }}
                            >
                                <option value=""></option>
                                <option value="APPROVED">{t('sample_orders.feedback_approved', '✓ Aprovado')}</option>
                                <option value="REJECTED">{t('sample_orders.feedback_rejected', '✗ Rejeitado')}</option>
                                <option value="CHANGES REQUIRED">{t('sample_orders.feedback_changes_required', '⚠ Requer Alterações')}</option>
                            </select>
                          </td>
                          
                                                   
                          <td colSpan={Math.ceil(sizeColumns.length / 2)} className="status-cell"></td>

                        </tr>



                        {/* Article Data Row 1: Product Code, Color, Quantities, Attachments */}
                        <tr className="article-row">
                          <td className="product-cell">
                            <input
                              type="text"
                              value={article.enteredProductCode || ''}
                              onChange={(e) => handleLineChange(articleIndex, 'enteredProductCode', e.target.value)}
                              placeholder={t('sample_orders.product_code', 'Product')}
                              className="form-control-sm"
                            />
                          </td>
                          <td className="color-cell">
                            <input
                              type="text"
                              value={article.enteredProductColor || ''}
                              onChange={(e) => handleLineChange(articleIndex, 'enteredProductColor', e.target.value)}
                              placeholder={t('sample_orders.color', 'Color')}
                              className="form-control-sm"
                            />
                          </td>
                          {sizeColumns.map(size => (
                            <td key={size.horz_value} className="quantity-cell">
                              <input
                                type="number"
                                value={formatRemoveDecimals(article.quantities[size.horz_value]) || ''}
                                onChange={(e) => handleQuantityChange(articleIndex, size.horz_value, e.target.value)}
                                placeholder=""
                                className="form-control-sm text-right"
                                min="0"
                                step="1"
                              />
                            </td>
                          ))}
                          {isNoSize && (
                            <td className="quantity-cell">
                              <input
                                type="number"
                                value={formatRemoveDecimals(article.quantities['__NONE__']) || ''}
                                onChange={(e) => handleQuantityChange(articleIndex, '__NONE__', e.target.value)}
                                placeholder=""
                                className="form-control-sm text-right"
                                min="0"
                                step="1"
                              />
                            </td>
                          )}
                          <td className="text-center attachment-cell" rowSpan={2}>
                            <button
                              type="button"
                              onClick={() => handleOpenLineAttachments(article, articleIndex)}
                              className="btn-attachment"
                              title={t('attachments.manage', 'Manage attachments')}
                            >
                              <FiPaperclip size={16} />
                              {/* Use matrixLineNo as key since that's how we store counts */}
                              {lineAttachmentCounts[article.matrixLineNo] > 0 && (
                                <span className="attachment-badge">
                                  {lineAttachmentCounts[article.matrixLineNo]}
                                </span>
                              )}
                            </button>
                          </td>


                          <td className="text-center attachment-cell" rowSpan={2}>
                            <button
                              type="button"
                              className="btn-action btn-tasks"
                              title={!isEditMode ? t('sample_orders.save_first', 'Guarde o pedido primeiro') : t('areas.tasks', 'Tarefas')}
                              disabled={!isEditMode || !article.matrixLineNo}
                              onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
setTasksModal({
  matrixLineNo: article.matrixLineNo,
  lineLabel:    `${article.enteredProductCode || ''} ${article.lineDescription || ''}`.trim(),
  lineArticle: {
    enteredProductCode:  article.enteredProductCode,
    enteredProductColor: article.enteredProductColor,
    erpProductCode:      article.erpProductCode,
    erpProductColorCode: article.erpProductColorCode,
  },
  orderCreatedAt: header.createdAt,
});
                              }}
                            >
                              <FiList size={15}/>
                            </button>
                          </td>



                          <td className="text-center attachment-cell" rowSpan={2}>
                            <button
                              type="button"
                              onClick={() => handleDuplicateLine(articleIndex)}
                              className="btn-action"
                              title={t('sample_orders.duplicate_line', 'Duplicar linha')}
                            >
                              <FiCopy size={15} />
                            </button>
                          </td>
                          <td className="text-center attachment-cell" rowSpan={2}>
                            <button
                              type="button"
                              onClick={() => handleDeleteLine(articleIndex)}
                              className="btn-action btn-delete"
                              title={t('delete', 'Delete')}
                            >
                              <FiTrash2 size={16} />
                            </button>
                          </td>

                          <td className="text-center attachment-cell" rowSpan={2}>
                            {/*
              {article.lineIds && Object.keys(article.lineIds).length > 0 && (AC
                <button
                  type="button"
                  onClick={() => {
                    setSelectedLineForAudit(article);
                    setShowLineAuditLog(true);
                  }}
                  className="btn-attachment"
                  title={t('audit.view_history', 'View Line History')}
                >
                  <FiClock size={16} />
                </button>
              )}
                */}

                          </td>
                    <td colSpan={sizeColumns.length + 1}></td>
                        </tr>

                        {/* Article Data Row 2: ERP Code, ERP Color, Description, Status */}
                        <tr className="article-row-erp">
                          <td className="erp-product-cell">
                            <input
                              type="text"
                              value={article.erpProductCode || ''}
                              onChange={(e) => handleLineChange(articleIndex, 'erpProductCode', e.target.value)}
                              placeholder={t('sample_orders.erp_code', 'ERP Code')}
                              className="form-control-sm"
                            />
                          </td>
                          <td className="erp-color-cell">
                            <input
                              type="text"
                              value={article.erpProductColorCode || ''}
                              onChange={(e) => handleLineChange(articleIndex, 'erpProductColorCode', e.target.value)}
                              placeholder={t('sample_orders.erp_color', 'ERP Color')}
                              className="form-control-sm"
                            />
                          </td>
                          <td className="description-cell" colSpan={sizeColumns.length}>
                            <input
                              type="text"
                              value={article.lineDescription || ''}
                              onChange={(e) => handleLineChange(articleIndex, 'lineDescription', e.target.value)}
                              placeholder={t('sample_orders.line_description', 'Description')}
                              className="form-control-sm"
                              maxLength={200}
                            />
                          </td>
   <td colSpan={sizeColumns.length + 1}></td>
                        </tr>

                        {/* Separator row */}
                        {/*
                        <tr className="article-separator">
                          <td colSpan={100}></td>
                        </tr>
*/}

                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </form>


      {/* Line Attachments Modal */}

      {showLineAttachmentsModal && currentLine && (
        <LineAttachmentsModal
          line={currentLine}
          orderPublicId={sampleOrderId}
          matrixLineNo={currentLine.matrixLineNo}
          entityType="sample_order_line"
          onClose={() => {
            setShowLineAttachmentsModal(false);
            setCurrentLine(null);
            setCurrentLineIndex(null);
            // Reload attachment counts after closing
            if (lines.length > 0) {
              loadLineAttachments(lines);
            }
          }}
          pendingMode={!currentLine.sample_order_line_id}
          pendingFiles={pendingLineFiles[currentLine.matrixLineNo] || []}
          onPendingFilesChange={(files) => {
            setPendingLineFiles(prev => ({
              ...prev,
              [currentLine.matrixLineNo]: files
            }));
          }}
        />
      )}

      {/* Toast notifications */}
      <div className="toast-container">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            id={toast.id}
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            requireConfirm={toast.requireConfirm}
            onConfirm={toast.onConfirm}
            onClose={removeToast}
          />
        ))}
      </div>

      {/* Comments Panel */}
      {isEditMode && internalOrderId && (
        <CommentsPanel
          entityType="sample_order_header"
          entityId={internalOrderId}
          isOpen={showCommentsPanel}
          onClose={() => {
            setShowCommentsPanel(false);
            loadCommentCount();
          }}
        />
      )}

      {/* Audit Log Panel */}
      {isEditMode && internalOrderId && (
        <AuditLogPanel
          isOpen={showAuditLog}
          onClose={() => setShowAuditLog(false)}
          entityType="sample_order_header"
          entityId={internalOrderId?.toString()}
        />
      )}

      {/* Audit Log Panel - Line */}
      {isEditMode && selectedLineForAudit && (
        <AuditLogPanel
          isOpen={showLineAuditLog}
          onClose={() => {
            setShowLineAuditLog(false);
            setSelectedLineForAudit(null);
          }}
          entityType="sample_order_line"
          entityId={getFirstLineId(selectedLineForAudit)}
          title={`${t('audit.line_history', 'Line History')} - ${t('sample_orders.article', 'Article')} #${selectedLineForAudit.matrixLineNo}`}
        />
      )}




      {/* Order Chain Panel */}
      <SampleOrderChainPanel
        isOpen={showChainPanel}
        onClose={() => setShowChainPanel(false)}
        publicId={sampleOrderId}
      />

      {/* Follow-Up Order Modal */}
      {showFollowUpModal && (
        <>
          <div
            className="modal-so-overlay"
            onClick={() => setShowFollowUpModal(false)}
          />
          <div className="modal-so-container">
            <div className="modal-so-header">
              <h3>{t('sample_orders.create_follow_up', 'Criar Pedido Revisão')}</h3>
              <button
                type="button"
                onClick={() => setShowFollowUpModal(false)}
                className="modal-so-close"
              >
                ×
              </button>
            </div>
            <div className="modal-so-content">
              <p style={{ marginBottom: '16px', color: 'var(--gray-600)', fontSize: '14px' }}>
                {t('sample_orders.follow_up_description', 'Criar um novo pedido de revisão baseado nos artigos selecionados')}
              </p>

              <div className="form-grid form-grid-2" style={{ gap: '16px' }}>
                <div className="form-group">
                  <label>{t('sample_orders.external_doc', 'Doc. Externo')}</label>
                  <input
                    type="text"
                    value={followUpData.externalDocument}
                    onChange={(e) => handleFollowUpDataChange('externalDocument', e.target.value)}
                    maxLength={20}
                  />
                </div>

                <div className="form-group">
                  <label>{t('sample_orders.order_date', 'Data Pedido')}</label>
                  <input
                    type="date"
                    value={followUpData.orderDate}
                    onChange={(e) => handleFollowUpDataChange('orderDate', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>{t('sample_orders.delivery_date', 'Data Entrega')}</label>
                  <input
                    type="date"
                    value={followUpData.requestedDeliveryDate}
                    onChange={(e) => handleFollowUpDataChange('requestedDeliveryDate', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>{t('sample_orders.priority', 'Prioridade')}</label>
                  <select
                    value={followUpData.priority}
                    onChange={(e) => handleFollowUpDataChange('priority', parseInt(e.target.value))}
                  >
                    <option value={1}>Alta</option>
                    <option value={2}>Média</option>
                    <option value={3}>Baixa</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>{t('sample_orders.division', 'Divisão')} *</label>
                  <SearchableSelect
                    value={followUpData.divisionId}
                    onChange={(value) => handleFollowUpDataChange('divisionId', value)}
                    options={divisions.map(d => ({
                      value: d.division_id,
                      code: d.division_code,
                      desc: d.description
                    }))}
                    getOptionLabel={(opt) => opt.desc ? `${opt.code} - ${opt.desc}` : opt.code}
                    getOptionValue={(opt) => opt.value}
                    formatDisplay={(opt) => opt.desc ? `${opt.code} - ${opt.desc}` : opt.code}
                    placeholder={t('select', 'Select...')}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>{t('sample_orders.production_type', 'Tipo Produção')} *</label>
                  <SearchableSelect
                    value={followUpData.productionTypeId}
                    onChange={(value) => handleFollowUpDataChange('productionTypeId', value)}
                    options={productionTypes.map(pt => ({
                      value: pt.production_type_id,
                      code: pt.production_type,
                      desc: pt.description
                    }))}
                    getOptionLabel={(opt) => opt.desc ? `${opt.code} - ${opt.desc}` : opt.code}
                    getOptionValue={(opt) => opt.value}
                    formatDisplay={(opt) => opt.desc ? `${opt.code} - ${opt.desc}` : opt.code}
                    placeholder={t('select', 'Select...')}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>{t('sample_orders.order_type', 'Tipo Pedido')} *</label>
                  <SearchableSelect
                    value={followUpData.sampleOrderTypeId}
                    onChange={(value) => handleFollowUpDataChange('sampleOrderTypeId', value)}
                    options={sampleOrderTypes.map(sot => ({
                      value: sot.sample_order_type_id,
                      code: sot.sample_order_type,
                      desc: sot.description
                    }))}
                    getOptionLabel={(opt) => opt.desc ? `${opt.code} - ${opt.desc}` : opt.code}
                    getOptionValue={(opt) => opt.value}
                    formatDisplay={(opt) => opt.desc ? `${opt.code} - ${opt.desc}` : opt.code}
                    placeholder={t('select', 'Select...')}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>{t('sample_orders.collection', 'Coleção')}</label>
                  <SearchableSelect
                    value={followUpData.collectionId}
                    onChange={(value) => handleFollowUpDataChange('collectionId', value)}
                    options={collections.map(col => ({
                      value: col.collection_id,
                      code: col.collection_code,
                      desc: col.description
                    }))}
                    getOptionLabel={(opt) => opt.desc ? `${opt.code} - ${opt.desc}` : opt.code}
                    getOptionValue={(opt) => opt.value}
                    formatDisplay={(opt) => opt.desc ? `${opt.code} - ${opt.desc}` : opt.code}
                    placeholder={t('select', 'Select...')}
                  />
                </div>

                <div className="form-group full-width">
                  <label>{t('sample_orders.description', 'Descrição')} *</label>
                  <textarea
                    value={followUpData.sampleOrderDescription}
                    onChange={(e) => handleFollowUpDataChange('sampleOrderDescription', e.target.value)}
                    rows={4}
                    required
                    style={{ width: '100%' }}
                  />
                </div>

                <div className="form-group full-width">
                  <label>{t('notes', 'Notas')}</label>
                  <textarea
                    value={followUpData.notes}
                    onChange={(e) => handleFollowUpDataChange('notes', e.target.value)}
                    rows={2}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>

              <div style={{
                marginTop: '20px',
                padding: '12px 16px',
                background: '#e0f2f1',
                borderRadius: '6px',
                border: '1px solid #b2dfdb'
              }}>
                <strong style={{ color: 'var(--primary)' }}>
                  {t('sample_orders.selected_articles', 'Artigos Selecionados')}:
                </strong>
                <span style={{ marginLeft: '8px', fontSize: '16px', fontWeight: '700' }}>
                  {selectedArticles.size}
                </span>
              </div>
            </div>
            <div className="modal-so-footer">
              <button
                type="button"
                onClick={() => setShowFollowUpModal(false)}
                className="btn btn-secondary"
              >
                {t('cancel', 'Cancelar')}
              </button>
              <button
                type="button"
                onClick={handleCreateFollowUp}
                className="btn btn-primary"
                disabled={saving || !followUpData.sampleOrderDescription}
              >
                {saving ? t('creating', 'A criar...') : t('create', 'Criar')}
              </button>
            </div>
          </div>
        </>
      )}


      {tasksModal && (
  <LineOperationsModal
    orderPublicId={sampleOrderId}
    orderId={internalOrderId}
    matrixLineNo={tasksModal.matrixLineNo}
    lineLabel={tasksModal.lineLabel}
    lineArticle={tasksModal.lineArticle}
    sampleOrderTypeId={header.sampleOrderTypeId}
    orderDate={header.orderDate}
    orderCreatedAt={tasksModal.orderCreatedAt}
    onClose={() => {
      setTasksModal(null);
      loadCommentCount();   // task comments count toward the order's comments
    }}
  />
)}

    </div>

  );


};

export default SampleOrderForm;